import React, { useRef } from "react";
import styled from "@emotion/styled";
import { Box } from "../../primitives/Box/Box";
import { FlexRow } from "../Layout/FlexRow/FlexRow";
import { Icon } from "../Icon/Icon";
import { colorPalette } from "../../theme/colors";
import type { DiscoverSearchFieldProps } from "./DiscoverSearchField.types";

const ICON_COLOR = colorPalette.gray[600];

const FONT_STACK =
  'Lexend, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const FieldInput = styled.input`
  flex: 1;
  min-width: 0;
  width: 100%;
  min-height: 44px;
  border: none;
  margin: 0;
  padding: 0;
  background: transparent;
  font-family: ${FONT_STACK};
  font-size: 16px;
  color: ${colorPalette.gray[900]};
  box-sizing: border-box;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: ${colorPalette.slate[500]};
  }
`;

/**
 * Hub / Discover brand search field (creator-v2 v1). Separate from SearchInput.
 */
export const DiscoverSearchField: React.FC<DiscoverSearchFieldProps> = ({
  value = "",
  placeholder = "Search brands",
  onChange,
  onFocus,
  onBlur,
  onKeyDown,
  "data-cy": dataCy,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <FlexRow
      alignItems="center"
      gap={10}
      width="100%"
      maxWidth="100%"
      minWidth={0}
      minHeight={48}
      px={14}
      backgroundColor={colorPalette.gray[300]}
      borderRadius={12}
      border={`1px solid ${colorPalette.slate[200]}`}
      cursor="text"
      onClick={() => inputRef.current?.focus()}
      role="presentation"
    >
      <Box flexShrink={0} display="flex" alignItems="center" aria-hidden>
        <Icon
          name="fa-solid fa-magnifying-glass"
          color={ICON_COLOR}
          height="16"
          width="16"
          size="14"
        />
      </Box>
      <FieldInput
        ref={inputRef}
        type="search"
        enterKeyHint="search"
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        autoComplete="off"
        autoCorrect="off"
        data-cy={dataCy}
      />
    </FlexRow>
  );
};

export default DiscoverSearchField;
